import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Auth Context for role-based access control
 * Roles: 'operator' | 'parent' | null
 */

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state synchronously from localStorage
  const getInitialUser = () => {
    const token = localStorage.getItem('access_token');
    const role = localStorage.getItem('user_role');
    const familyId = localStorage.getItem('family_id');
    const parentId = localStorage.getItem('parent_id');
    const fullName = localStorage.getItem('parent_name');

    if (role) {
      return {
        token,
        role, // 'operator' | 'parent'
        familyId,
        parentId,
        fullName,
      };
    }
    return null;
  };

  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(false); // No async loading needed
  const navigate = useNavigate();

  // Silent JWT refresh — refresh when token is within 1 hour of expiry
  useEffect(() => {
    if (!user?.token || user?.role !== 'parent') return;

    const checkAndRefresh = async () => {
      try {
        const payload = JSON.parse(atob(user.token.split('.')[1]));
        const timeUntilExpiry = payload.exp * 1000 - Date.now();
        if (timeUntilExpiry < 60 * 60 * 1000 && timeUntilExpiry > 0) {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${user.token}` },
          });
          const data = await res.json();
          if (data.ok && data.access_token) {
            localStorage.setItem('access_token', data.access_token);
            setUser(prev => prev ? { ...prev, token: data.access_token } : prev);
          }
        }
      } catch (err) {
        // Silent failure — user re-logs at expiry
      }
    };

    checkAndRefresh();
    const interval = setInterval(checkAndRefresh, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.token, user?.role]);

  const loginAsParent = (token, familyId, parentId, fullName) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('user_role', 'parent');
    localStorage.setItem('family_id', familyId);
    localStorage.setItem('parent_id', parentId);
    if (fullName) localStorage.setItem('parent_name', fullName);

    setUser({
      token,
      role: 'parent',
      familyId,
      parentId,
      fullName: fullName || null,
    });
  };

  const loginAsOperator = (token) => {
    // Store the operator token
    if (token) {
      localStorage.setItem('access_token', token);
    }
    localStorage.setItem('user_role', 'operator');
    localStorage.removeItem('family_id');
    localStorage.removeItem('parent_id');
    
    setUser({
      token: token || localStorage.getItem('access_token'),
      role: 'operator',
      familyId: null,
      parentId: null,
    });
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('family_id');
    localStorage.removeItem('parent_id');
    localStorage.removeItem('parent_name');
    setUser(null);
    navigate('/');
  };

  const isParent = () => user?.role === 'parent';
  const isOperator = () => user?.role === 'operator';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      loginAsParent,
      loginAsOperator,
      logout,
      isParent,
      isOperator,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
